/* eslint-disable no-nested-ternary */
import React, { useEffect, useMemo, useState } from 'react';
import { IoCloseCircleOutline } from 'react-icons/io5';
import { Button, Flex, Spinner, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { PageBox } from '../../components/pageBox';
import { ReturnButton } from '../../components/buttons/ReturnButton';
import { FormBox } from '../../components/formBox';
import { IData } from '../../types/shared';
import { useCompanies, IBreakRange } from '../../hooks/useCompanies';
import { formatCNPJ } from '../../utils/FormatFunctions';

const calcularTotalRange = (item: IBreakRange) => item.numero_final - item.numero_inicial + 1;

export function BreaksPage() {
  const location = useLocation();
  const [company] = useState(location.state?.company);
  const { getQuebrasWithId } = useCompanies();
  const { itemId } = useParams();
  const [searchParams] = useSearchParams();
  const dateStart = searchParams.get('date_start');
  const dateEnd = searchParams.get('date_end');

  const [NFEList, setNFEList] = useState<IBreakRange[]>([]);
  const [NFSEList, setNFSEList] = useState<IBreakRange[]>([]);
  const [NFCEList, setNFCEList] = useState<IBreakRange[]>([]);
  const [CTEList, setCTEList] = useState<IBreakRange[]>([]);

  // Apenas páginas em estado - resto vem da URL
  const [pageNFE, setPageNFE] = useState(1);
  const [pageNFSE, setPageNFSE] = useState(1);
  const [pageNFCE, setPageNFCE] = useState(1);
  const [pageCTE, setPageCTE] = useState(1);

  // Verifica se temos params válidos da URL
  const hasValidParams = Boolean(itemId && dateStart && dateEnd);

  // Deriva os filtros diretamente dos params da URL
  const filtersNFE = useMemo(
    () => ({
      page: pageNFE.toString(),
      size: '30',
      nf_type: 'nfe',
      date_start: dateStart,
      date_end: dateEnd,
      companyId: itemId,
    }),
    [pageNFE, dateStart, dateEnd, itemId],
  );

  const filtersNFSE = useMemo(
    () => ({
      page: pageNFSE.toString(),
      size: '30',
      nf_type: 'nfse',
      date_start: dateStart,
      date_end: dateEnd,
      companyId: itemId,
    }),
    [pageNFSE, dateStart, dateEnd, itemId],
  );

  const filtersNFCE = useMemo(
    () => ({
      page: pageNFCE.toString(),
      size: '30',
      nf_type: 'nfce',
      date_start: dateStart,
      date_end: dateEnd,
      companyId: itemId,
    }),
    [pageNFCE, dateStart, dateEnd, itemId],
  );

  const filtersCTE = useMemo(
    () => ({
      page: pageCTE.toString(),
      size: '30',
      nf_type: 'cte',
      date_start: dateStart,
      date_end: dateEnd,
      companyId: itemId,
    }),
    [pageCTE, dateStart, dateEnd, itemId],
  );

  // Limpa listas quando params da URL mudam
  useEffect(() => {
    setNFEList([]);
    setNFSEList([]);
    setNFCEList([]);
    setCTEList([]);
    setPageNFE(1);
    setPageNFSE(1);
    setPageNFCE(1);
    setPageCTE(1);
  }, [itemId, dateStart, dateEnd]);

  const { data: dataBreaksNFE, isFetching: isFetchingBreaksNFE } = useQuery<IData<IBreakRange[]>>({
    queryKey: ['breaksNFE', filtersNFE],
    queryFn: getQuebrasWithId,
    enabled: hasValidParams,
  });
  const { data: dataBreaksNFSE, isFetching: isFetchingBreaksNFSE } = useQuery<IData<IBreakRange[]>>(
    {
      queryKey: ['breaksNFSE', filtersNFSE],
      queryFn: getQuebrasWithId,
      enabled: hasValidParams,
    },
  );
  const { data: dataBreaksNFCE, isFetching: isFetchingBreaksNFCE } = useQuery<IData<IBreakRange[]>>(
    {
      queryKey: ['breaksNFCE', filtersNFCE],
      queryFn: getQuebrasWithId,
      enabled: hasValidParams,
    },
  );
  const { data: dataBreaksCTE, isFetching: isFetchingBreaksCTE } = useQuery<IData<IBreakRange[]>>({
    queryKey: ['breaksCTE', filtersCTE],
    queryFn: getQuebrasWithId,
    enabled: hasValidParams,
  });

  const dataNFE = dataBreaksNFE?.data;
  const dataNFSE = dataBreaksNFSE?.data;
  const dataNFCE = dataBreaksNFCE?.data;
  const dataCTE = dataBreaksCTE?.data;

  useEffect(() => {
    if (dataNFE) {
      if (dataNFE?.page === 1) {
        setNFEList(dataNFE.items);
      } else {
        setNFEList(old => [...old, ...dataNFE.items]);
      }
    }
  }, [dataNFE]);

  useEffect(() => {
    if (dataNFSE) {
      if (dataNFSE?.page === 1) {
        setNFSEList(dataNFSE.items);
      } else {
        setNFSEList(old => [...old, ...dataNFSE.items]);
      }
    }
  }, [dataNFSE]);

  useEffect(() => {
    if (dataNFCE) {
      if (dataNFCE?.page === 1) {
        setNFCEList(dataNFCE.items);
      } else {
        setNFCEList(old => [...old, ...dataNFCE.items]);
      }
    }
  }, [dataNFCE]);

  useEffect(() => {
    if (dataCTE) {
      if (dataCTE?.page === 1) {
        setCTEList(dataCTE.items);
      } else {
        setCTEList(old => [...old, ...dataCTE.items]);
      }
    }
  }, [dataCTE]);

  return (
    <PageBox>
      <div className="flex flex-col gap-2">
        <div className="flex flex-col items-center justify-between gap-3 py-5 lg:flex-row">
          <div className="flex content-start md:w-1/3 lg:content-center ">
            <ReturnButton previousPage="modulesHome/integration/sequenceBreak" label="Voltar" />
            <div className="flex flex-col items-center gap-3 p-5 lg:flex-row">
              <div className="h-full">
                <div className="flex items-center justify-center rounded-full bg-[#FFE0EB] p-3">
                  <IoCloseCircleOutline color="#FF82AC" className="size-12" />
                </div>
              </div>
              <div className="flex w-full flex-col gap-1">
                <Text fontWeight="bold" fontSize="16px">
                  Quebras
                </Text>
              </div>
            </div>
          </div>
          <div className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-3">
            <Flex className="flex cursor-pointer items-center gap-3 rounded-3xl bg-body p-5">
              <div className="flex w-full flex-col ">
                <Text fontSize="10px" color="#718EBF">
                  ID
                </Text>
                <Text fontSize="14px" fontWeight="bold">
                  {company?.external_id || itemId || '-'}
                </Text>
              </div>
            </Flex>
            <Flex className="flex cursor-pointer items-center gap-3 rounded-3xl bg-body p-5">
              <div className="flex w-full flex-col ">
                <Text fontSize="10px" color="#718EBF">
                  CNPJ
                </Text>
                <Text fontSize="14px" fontWeight="bold">
                  {company?.cnpj ? formatCNPJ(company.cnpj) : '-'}
                </Text>
              </div>
            </Flex>
            <Flex className="flex cursor-pointer items-center gap-3 rounded-3xl bg-body p-5">
              <div className="flex w-full flex-col ">
                <Text fontSize="10px" color="#718EBF">
                  Razão social
                </Text>
                <Text fontSize="14px" fontWeight="bold">
                  {company?.razao_social || '-'}
                </Text>
              </div>
            </Flex>
          </div>
        </div>

        <FormBox>
          <div className="flex flex-col gap-4 p-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              <div className="flex flex-col gap-4 rounded-lg border border-border bg-body p-4 shadow-lg">
                <div className="flex flex-col gap-2">
                  <Text fontSize="2xl" fontWeight="bold" textAlign="center" color="blue.600">
                    NFE
                    {dataNFE?.total_notas != null &&
                      dataNFE.total_notas > 0 &&
                      `: ${dataNFE.total_notas}`}
                  </Text>
                  <div>
                    {isFetchingBreaksNFE && NFEList.length === 0 ? (
                      <div className="flex flex-col items-center gap-4">
                        <Spinner size="lg" color="primary" />
                        <span className="text-xl font-semibold">Carregando...</span>
                      </div>
                    ) : dataNFE?.items.length === 0 ? (
                      <div className="flex flex-col items-center gap-4">Nenhum item encontrado</div>
                    ) : (
                      <div className="flex flex-col gap-2">
                        {NFEList?.map(item => (
                          <div
                            key={`nfe-${item.numero_inicial}-${item.numero_final}-${item.serie}-${item.codi_esp}`}
                            className="rounded-lg border border-blue-200 bg-blue-50 p-3"
                          >
                            <div className="flex items-center justify-between">
                              <Text fontSize="sm" fontWeight="bold" color="blue.700">
                                {item.numero_inicial} - {item.numero_final}
                              </Text>
                              <Text fontSize="xs" fontWeight="bold" color="blue.500">
                                {calcularTotalRange(item)} nota(s)
                              </Text>
                            </div>
                            <Text fontSize="xs" color="gray.600">
                              Série: {item.serie} | Espécie: {item.codi_esp}
                            </Text>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {isFetchingBreaksNFE && NFEList.length > 0 && (
                    <div className="flex flex-col items-center gap-4">
                      <Spinner size="lg" color="primary" />
                    </div>
                  )}

                  {dataNFE && NFEList.length > 0 && dataNFE.next_page && (
                    <div>
                      <Button variant="ghost" onClick={() => setPageNFE(p => p + 1)}>
                        Ver mais
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-4 rounded-lg border border-border bg-body p-4 shadow-lg">
                <div className="flex flex-col gap-2">
                  <Text fontSize="2xl" fontWeight="bold" textAlign="center" color="green.600">
                    NFSE
                    {dataNFSE?.total_notas != null &&
                      dataNFSE.total_notas > 0 &&
                      `: ${dataNFSE.total_notas}`}
                  </Text>
                  <div>
                    {isFetchingBreaksNFSE && NFSEList.length === 0 ? (
                      <div className="flex flex-col items-center gap-4">
                        <Spinner size="lg" color="primary" />
                        <span className="text-xl font-semibold">Carregando...</span>
                      </div>
                    ) : dataNFSE?.items.length === 0 ? (
                      <div className="flex flex-col items-center gap-4">Nenhum item encontrado</div>
                    ) : (
                      <div className="flex flex-col gap-2">
                        {NFSEList?.map(item => (
                          <div
                            key={`nfse-${item.numero_inicial}-${item.numero_final}-${item.serie}-${item.codi_esp}`}
                            className="rounded-lg border border-green-200 bg-green-50 p-3"
                          >
                            <div className="flex items-center justify-between">
                              <Text fontSize="sm" fontWeight="bold" color="green.700">
                                {item.numero_inicial} - {item.numero_final}
                              </Text>
                              <Text fontSize="xs" fontWeight="bold" color="green.500">
                                {calcularTotalRange(item)} nota(s)
                              </Text>
                            </div>
                            <Text fontSize="xs" color="gray.600">
                              Série: {item.serie} | Espécie: {item.codi_esp}
                            </Text>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {isFetchingBreaksNFSE && NFSEList.length > 0 && (
                    <div className="flex flex-col items-center gap-4">
                      <Spinner size="lg" color="primary" />
                    </div>
                  )}

                  {dataNFSE && NFSEList.length > 0 && dataNFSE.next_page && (
                    <div>
                      <Button variant="ghost" onClick={() => setPageNFSE(p => p + 1)}>
                        Ver mais
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-4 rounded-lg border border-border bg-body p-4 shadow-lg">
                <div className="flex flex-col gap-2">
                  <Text fontSize="2xl" fontWeight="bold" textAlign="center" color="purple.600">
                    NFCE
                    {dataNFCE?.total_notas != null &&
                      dataNFCE.total_notas > 0 &&
                      `: ${dataNFCE.total_notas}`}
                  </Text>
                  <div>
                    {isFetchingBreaksNFCE && NFCEList.length === 0 ? (
                      <div className="flex flex-col items-center gap-4">
                        <Spinner size="lg" color="primary" />
                        <span className="text-xl font-semibold">Carregando...</span>
                      </div>
                    ) : dataNFCE?.items.length === 0 ? (
                      <div className="flex flex-col items-center gap-4">Nenhum item encontrado</div>
                    ) : (
                      <div className="flex flex-col gap-2">
                        {NFCEList?.map(item => (
                          <div
                            key={`nfce-${item.numero_inicial}-${item.numero_final}-${item.serie}-${item.codi_esp}`}
                            className="rounded-lg border border-purple-200 bg-purple-50 p-3"
                          >
                            <div className="flex items-center justify-between">
                              <Text fontSize="sm" fontWeight="bold" color="purple.700">
                                {item.numero_inicial} - {item.numero_final}
                              </Text>
                              <Text fontSize="xs" fontWeight="bold" color="purple.500">
                                {calcularTotalRange(item)} nota(s)
                              </Text>
                            </div>
                            <Text fontSize="xs" color="gray.600">
                              Série: {item.serie} | Espécie: {item.codi_esp}
                            </Text>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {isFetchingBreaksNFCE && NFCEList.length > 0 && (
                    <div className="flex flex-col items-center gap-4">
                      <Spinner size="lg" color="primary" />
                    </div>
                  )}

                  {dataNFCE && NFCEList.length > 0 && dataNFCE.next_page && (
                    <div>
                      <Button variant="ghost" onClick={() => setPageNFCE(p => p + 1)}>
                        Ver mais
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-4 rounded-lg border border-border bg-body p-4 shadow-lg">
                <div className="flex flex-col gap-2">
                  <Text fontSize="2xl" fontWeight="bold" textAlign="center" color="orange.600">
                    CTE
                    {dataCTE?.total_notas != null &&
                      dataCTE.total_notas > 0 &&
                      `: ${dataCTE.total_notas}`}
                  </Text>
                  <div>
                    {isFetchingBreaksCTE && CTEList.length === 0 ? (
                      <div className="flex flex-col items-center gap-4">
                        <Spinner size="lg" color="primary" />
                        <span className="text-xl font-semibold">Carregando...</span>
                      </div>
                    ) : dataCTE?.items.length === 0 ? (
                      <div className="flex flex-col items-center gap-4">Nenhum item encontrado</div>
                    ) : (
                      <div className="flex flex-col gap-2">
                        {CTEList?.map(item => (
                          <div
                            key={`cte-${item.numero_inicial}-${item.numero_final}-${item.serie}-${item.codi_esp}`}
                            className="rounded-lg border border-orange-200 bg-orange-50 p-3"
                          >
                            <div className="flex items-center justify-between">
                              <Text fontSize="sm" fontWeight="bold" color="orange.700">
                                {item.numero_inicial} - {item.numero_final}
                              </Text>
                              <Text fontSize="xs" fontWeight="bold" color="orange.500">
                                {calcularTotalRange(item)} nota(s)
                              </Text>
                            </div>
                            <Text fontSize="xs" color="gray.600">
                              Série: {item.serie} | Espécie: {item.codi_esp}
                            </Text>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {isFetchingBreaksCTE && CTEList.length > 0 && (
                    <div className="flex flex-col items-center gap-4">
                      <Spinner size="lg" color="primary" />
                    </div>
                  )}

                  {dataCTE && CTEList.length > 0 && dataCTE.next_page && (
                    <div>
                      <Button variant="ghost" onClick={() => setPageCTE(p => p + 1)}>
                        Ver mais
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </FormBox>
      </div>
    </PageBox>
  );
}
